import { gql, useQuery } from '@apollo/client'
import { useEffect } from 'react'

import { SHORTCUT_PRODUCT_CARD_FIELDS, SHORTCUT_PRODUCT_FIELDS, SHORTCUT_PRODUCT_SHOP_INFO } from '@shared/api/shortcuts'
import useFetchMorePagination from '@shared/hooks/useFetchMorePagination'

import type { GQL_ID, NestedFilterInput, PageInfo, Product, ProductFilter } from '@@types'


type SearchQueryType = {
  search: string
  filters: {
    category_id?: GQL_ID | null
    saledOnly?: boolean
    price?: {
      gt?: number | null
      lt?: number | null
    }
  }
}

export type SearchProductsQuery = {
  searchProducts: {
    edges: Product[]
    pageInfo: PageInfo
    filters: ProductFilter[]
  }
}

export type SearchProductsVariables = {
  shop_id: GQL_ID
  searchQuery: SearchQueryType
  filter?: NestedFilterInput
  page?: number
  limit?: number
  style?: string
  skip?: boolean
}

export const GQL_QUERY_SEARCH_PRODUCTS = gql`
  query searchProducts (
    $shop_id: ID!
    $searchQuery: SearchQuery!
    $filter: NestedFilterInput
    $style: String
    $page: Int
    $limit: Int
  ) {
    searchProducts(
      shop_id: $shop_id
      searchQuery: $searchQuery
      filter: $filter
      _page: $page
      _limit: $limit
    ) {
      edges {
        ${SHORTCUT_PRODUCT_FIELDS}
        ${SHORTCUT_PRODUCT_CARD_FIELDS}
        ${SHORTCUT_PRODUCT_SHOP_INFO}
      }
      pageInfo {
        page
        lastPage
        perPage
        total
      }
      filters {
        id
        label
        type
        filterInputFieldName
        op
        values {
          value
          name
        }
      }
    }
  }
`

export const useSearchProducts = (variables: SearchProductsVariables) => {
  const { data, loading, fetchMore, ...rest} = useQuery<SearchProductsQuery, SearchProductsVariables>(GQL_QUERY_SEARCH_PRODUCTS, {
    variables,
    skip: variables.skip ?? !variables.shop_id
  })

  const products = data?.searchProducts.edges ?? []
  const filters = data?.searchProducts.filters ?? []
  const pageInfo = data?.searchProducts.pageInfo

  const { hasMore, loadingMore, fetchNextPage, clearPagination } =
    useFetchMorePagination<SearchProductsQuery, SearchProductsVariables>({
      loading: loading,
      fetchMore: fetchMore,
      variables: {
        shop_id: variables.shop_id,
        limit: variables.limit,
        searchQuery: variables.searchQuery,
        filter: variables.filter
      },
      updaterFunction: (cached, fetched) => {
        const prev = cached?.searchProducts?.edges ?? []
        const curr = fetched?.searchProducts?.edges ?? []

        return {
          hasMore: curr.length > 0,
          result: {
            ...cached,
            searchProducts: {
              ...cached.searchProducts,
              edges: [ ...prev, ...curr ],
              pageInfo:
                fetched.searchProducts?.pageInfo ||
                cached.searchProducts?.pageInfo,
            },
          },
        }
      },
    })

  
  useEffect(() => {
    clearPagination()
  }, [ variables.filter, variables.searchQuery ])

  return {
    data,
    products,
    loading,
    filters,
    pageInfo,
    hasMore,
    fetchNextPage,
    clearPagination,
    loadingMore,
    ...rest
  }
}
